import { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { EOrganization, ESnapshotExists, exists } from 'lib/types';
import PlacementActions, { selectOriginalId } from 'redux/placement';
import { selectIsPublisher } from 'redux/auth';
import { getBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import LinkTo from 'components/LinkTo';
import { getFirebaseContext } from 'utils/firebase';
import { useFetchAvailablePublishers } from 'hooks/useFetchAvailablePublishers';
import { Product } from 'lib/enums';
import { FORM_ROUTES } from 'router/routes';
import { getDynamicRoute } from 'lib/frontend/utils/router';
import ScrollStep from '../ScrollStep';
import { CONFIRM_PUBLISHER } from '../helpers/calculatePlacementSteps';
import { useFetchCustomer } from '../hooks/useFetchCustomer';
import {
  AdjudicationFilters,
  getStateFromLocationParams
} from '../helpers/publisherSearch';
import { selectRestrictedPublisherIds } from '../placeScrollSelectors';
import FastConfirmPublisherForm from './FastConfirmPublisherForm';
import { getIsAccountOnHold } from '../ConfirmFilerStep/accountHoldUtils';

type ConfirmPublisherStepProps = {
  next: () => void;
  onDisabledStepClick: (stepId: string) => void;
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
};

export default function ConfirmPublisherStep({
  next,
  onDisabledStepClick,
  publisherOrganization
}: ConfirmPublisherStepProps) {
  const dispatch = useAppDispatch();

  const restrictedSingleState = getStateFromLocationParams();

  const isUserPublisher = useAppSelector(selectIsPublisher);

  const restrictedPublisherIds = useAppSelector(selectRestrictedPublisherIds);

  const [adjudicationFilters, setAdjudicationFilters] =
    useState<AdjudicationFilters>({});

  const {
    loading: publisherListLoading,
    stateOptions,
    stateFilter,
    setStateFilter,
    publisherOptions,
    searchText,
    setSearchText,
    columnCertifiedFilter,
    setColumnCertifiedFilter
  } = useFetchAvailablePublishers({
    restrictedSingleState,
    isUserPublisher,
    restrictedPublisherIds,
    product: Product.Notice,
    adjudicationFilters
  });

  const originalId = useAppSelector(selectOriginalId);

  const customer = useFetchCustomer();

  const enableColumnExpressLink = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_CANT_FIND_NEWSPAPER
  );

  function handlePublisherOrganizationChange(publisherOrganizationId: string) {
    if (!publisherOrganizationId) {
      return dispatch(PlacementActions.setNewspaper(null));
    }

    const newPublisherOrganizationRef = getFirebaseContext()
      .organizationsRef()
      .doc(publisherOrganizationId);

    dispatch(PlacementActions.setNewspaper(newPublisherOrganizationRef));
  }

  function handleStateFilterChange(stateId?: number) {
    if (
      stateId &&
      exists(publisherOrganization) &&
      publisherOrganization.data().state !== stateId
    ) {
      dispatch(PlacementActions.setNewspaper(null));
    }

    setStateFilter(stateId);
  }

  function handleAdjudicationFilterChange(filters: AdjudicationFilters) {
    if (
      filters &&
      exists(publisherOrganization) &&
      JSON.stringify(adjudicationFilters) !== JSON.stringify(filters)
    ) {
      dispatch(PlacementActions.setNewspaper(null));
    }
    setAdjudicationFilters(filters);
  }

  const { isAccountOnHold, onHoldMessage } = getIsAccountOnHold(
    publisherOrganization,
    customer || null,
    { isPublisher: isUserPublisher }
  );

  const title = 'Where would you like to place a notice?';

  const caption =
    enableColumnExpressLink && originalId ? (
      <>
        <span>Can't find your local newspaper? </span>
        <LinkTo
          className="text-primary-500"
          href={getDynamicRoute(FORM_ROUTES.NEWSPAPER_WITH_NOTICE, {
            noticeId: originalId
          })}
        >
          Click here to let us know.
        </LinkTo>
      </>
    ) : (
      'Select a publication. Use the filter button to search by location.'
    );

  const isComplete =
    !!publisherOrganization && !isAccountOnHold && !publisherListLoading;

  return (
    <ScrollStep
      id={CONFIRM_PUBLISHER}
      next={next}
      complete={isComplete}
      onDisabledStepClick={onDisabledStepClick}
      title={title}
      caption={caption}
      onHoldText={onHoldMessage}
    >
      <FastConfirmPublisherForm
        publisherOrganization={publisherOrganization}
        onPublisherOrganizationChange={handlePublisherOrganizationChange}
        searchText={searchText}
        setSearchText={setSearchText}
        publisherOptions={publisherOptions}
        stateOptions={stateOptions}
        onStateChange={handleStateFilterChange}
        onAdjudicationFilterChange={handleAdjudicationFilterChange}
        activeFilters={{
          locationFilter: { stateFilter },
          adjudicationFilter: adjudicationFilters
        }}
        loading={publisherListLoading}
        columnCertifiedFilter={columnCertifiedFilter}
        setColumnCertifiedFilter={setColumnCertifiedFilter}
      />
    </ScrollStep>
  );
}
